/**
 * @file Recognise another app's built-in browser and build the links that
 * hand the call over to the phone's real browser.
 *
 * Design context: calls break inside built-in browsers (sound that stops,
 * no camera, no background audio), so the join screen offers a way out and
 * the call report names the app. Both read `detectInAppBrowser`, so the two
 * can never disagree about what counts as in-app. Detection only reads the
 * user agent: an app that hides its token is caught by the generic WebView
 * rules, and one that looks exactly like Safari (WhatsApp on iOS) is
 * accepted as Safari.
 */

// Apps whose built-in browser names itself in the user agent. Messenger before
// Facebook: both carry FBAN/FB_IAB, only Messenger's says Messenger/Orca.
const NAMED_APPS: Array<[string, RegExp]> = [
  ["Messenger", /FBAN\/Messenger|FB_IAB\/(?:Orca|MESSENGER)/],
  ["Facebook", /FBAN\/|FBAV\/|FB_IAB\//],
  ["Instagram", /\bInstagram \d/],
  ["LINE", /\bLine\/\d/],
  ["Google app", /\bGSA\/\d/],
  ["TikTok", /musical_ly|BytedanceWebview/],
  ["Snapchat", /\bSnapchat\/\d/],
  ["LinkedIn", /LinkedInApp/],
  ["X", /Twitter for iP|TwitterAndroid/],
  ["Telegram", /Telegram-Android/],
];

export type InAppPlatform = "ios" | "android" | "other";

export interface InAppBrowser {
  /** The app's name when it identifies itself, else null. */
  app: string | null;
  platform: InAppPlatform;
}

const platformOf = (ua: string): InAppPlatform =>
  /iPhone|iPad|iPod/.test(ua) ? "ios" : /Android/.test(ua) ? "android" : "other";

/** Real browsers on iOS name themselves; anything else without "Safari/" is a web view. */
const IOS_BROWSER = /Version\/\d+.*Safari\/|CriOS\/|FxiOS\/|EdgiOS\//;

/** null for a regular browser and for the WeCo desktop app. */
export function detectInAppBrowser(userAgent: string): InAppBrowser | null {
  if (/\bWeCoDesktop\//.test(userAgent)) return null;
  const platform = platformOf(userAgent);
  const app = NAMED_APPS.find(([, re]) => re.test(userAgent))?.[0] ?? null;
  if (app) return { app, platform };
  if (platform === "ios" && !IOS_BROWSER.test(userAgent) && !/Safari\//.test(userAgent)) {
    return { app: null, platform };
  }
  // Android System WebView says Chrome but carries "; wv)".
  if (platform === "android" && /;\s*wv\)/.test(userAgent)) return { app: null, platform };
  return null;
}

/** iOS opens `x-safari-https://…` in Safari from most built-in browsers. */
export function safariHandoffUrl(href: string): string {
  return href.replace(/^(https?):\/\//, "x-safari-$1://");
}

/** Android intent that opens this exact page in Chrome. */
export function chromeHandoffUrl(href: string): string {
  const url = new URL(href);
  const scheme = url.protocol.replace(":", "");
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${scheme};package=com.android.chrome;end`;
}
